import { useCurrentUser } from "entities/session/model/sessionModel";
import { toast } from "react-toastify";
import { ResponseCodes, sircapApi } from "shared/api/sircap";
import { OpenOrderDto, CalculateBondPriceDto } from "shared/api/sircap/Api";
import {
  AssetType,
  OperationStatus,
  OperationType,
  OrderTifType,
  OrderType,
} from "static/types";
import { createStore, useStore } from "zustand";
import { persist } from "zustand/middleware";
import moment from "moment-timezone";
import { accountStore } from "entities/account";
import { MultiLangField } from "helpers/functions";
import { strapiApi } from "shared/api/strapi";
import {
  normalizeStrapiResearchList,
  toLegacyResearchByAlias,
} from "entities/strapi/research";
import {
  BLOG_LIST_FIELDS,
  BLOG_LOCALES,
  collectBlogCategories,
  foldBlogLocales,
} from "entities/strapi/blog";
import { fxOrderStore } from "entities/fx-order";
import { ExchangeTimeFrame } from "hooks/useExchangeTimeFrame";
import { Capacitor } from "@capacitor/core";

export enum CouponType {
  Zero = "zero",
  Fixed = "fixed",
  Floating = "floating",
}
interface GetAssetsListDto {
  type?: string;
  sortBy?: string;
  orderBy?: string;
  page: number;
  limit: number;
}
export interface IAsset {
  id: number;
  symbol: string;
  isin: string;
  name: string;
  type: number;
  ask: number;
  bid: number;
  price: number;
  currency: {
    code: string;
    symbol: string;
  };
  exchange: null | IExchange;
  data: any;
  lot_size: number;
  minimal_quantity: number;
  minimal_commission: number;
  change: number;
  image: string;
  updatedAt?: string;
}
type IndexDataItemType =
  | {
      type: "text";
      label?: MultiLangField;
      value: MultiLangField;
    }
  | {
      type: "params";
      label?: MultiLangField;
      value: Array<
        | {
            type: "text";
            label: MultiLangField;
            value: MultiLangField;
          }
        | {
            type: "array";
            label: MultiLangField;
            value: any[];
          }
      >;
    }
  | {
      type: "image";
      description?: MultiLangField;
      caption?: MultiLangField;
      label?: MultiLangField;
      text?: MultiLangField;
      value: MultiLangField;
    }
  | {
      type: "list";
      label?: MultiLangField;
      text?: MultiLangField;
      caption?: MultiLangField;
      value: MultiLangField[];
    }
  | {
      type: "companies";
      label: MultiLangField;
      value: Array<{
        name: MultiLangField;
        description: MultiLangField;
        images: Array<MultiLangField>;
      }>;
    }
  | {
      type: "companies_list";
      label?: MultiLangField;
      value: Array<{
        name: string;
        logo: string;
        data: Array<{
          label: MultiLangField;
          value: MultiLangField | any;
          isLink?: boolean;
        }>;
      }>;
    }
  | {
      type: "references";
      value: Array<{
        title: MultiLangField;
        list: Array<{
          label: MultiLangField;
          link: MultiLangField;
        }>;
      }>;
    }
  | {
      type: "references_list";
      value: Array<{
        value: MultiLangField;
      }>;
    }
  | {
      type: "disclaimer";
      text: MultiLangField;
    }
  | {
      type: "template";
      value: string;
    }
  | {
      type: "chart";
      value: number;
    };

type IndexDataType = Array<IndexDataItemType>;

interface IndexInfoType {
  invest_chart_id?: number;
  logo: string;
  card: MultiLangField;
  background: {
    from: string;
    to: string;
  };
  disable_trading?: boolean;
  min_deposit: number;
  max_deposit: number;
  currencies?: Array<{
    code: string;
    min_deposit: number;
    max_deposit: number;
    alert_text?: MultiLangField;
    step?: number;
  }>;
  header_cards?: Array<{
    p: MultiLangField;
    span: MultiLangField;
  }>;
  header_image?: MultiLangField;
  hide_header_cards?: boolean;
  hide_increase_decrease?: boolean;
  telegram?: string;
  tabs: Array<{
    title: MultiLangField;
    data: IndexDataType;
  }>;
  agreements?: Array<{
    label: MultiLangField;
    text?: MultiLangField;
  }>;
  contract_type: string | null;
  contract_amount_type: null | "amount" | "shares";
  can_trade_from_date?: string; // YYYY-MM-DD
  allocation_results?: {
    title: MultiLangField;
    span: MultiLangField;
    company_column_text: MultiLangField;
    shares_column_text: MultiLangField;
    companies: string[];
    users: Array<{
      user_id: number;
      user_type: string;
      shares: Array<number | null>;
    }>;
  } | null;
  is_ipo?: boolean;
}

export interface IAssetInfo {
  asset: IAsset | null;
  info: {
    symbol: string;
    mktCap: number;
    companyName: string;
    currency: string;
    isin: string;
    sector: string;
    industry: string;
    description: string;
    country: string;
    image: string;
    exchange: string;
  };
  bondInfo: {
    country: string;
    ask: number;
    bid: number;
    nominal: number;
    ai: number;
    ask_yield: number;
    bid_yield: number;
    coupon_rate: number;
    coupon_frequency: number | null;
    coupon_type: string;
    last_coupon_date: string | null;
    next_coupon_date: string | null;
    risk: number | null;
    bond_type: string | null;
    maturity_date: string;
    settlement_date: string;
    call_date: string | null;
    put_date: string | null;
    is_otc: boolean;
    is_eurobond: boolean;
    is_fractional: boolean;
  };
  decimal_places?: number;
  available_order_types?: OrderType[];
  available_tif_types?: OrderTifType[];
  available_pre_post_orders?: boolean;
  available_marginal_trading?: boolean;
  tv_ticker?: string | null;
  dividends: {
    symbol: string;
    historical: Array<{
      date: string;
      label: string;
      adjDividend: number;
      dividend: number;
      recordDate: string;
      paymentDate: string;
      declarationDate: string;
    }>;
  };
  commission: number;
  minimal_commission: number;
  min_commission_currency?: string | null;
  min_commission_cb_rate?: number | null;
  account?: {
    currency: string;
    quantity: number;
    price: number;
    date: Date;
    data: any;
    asset: any;
  } | null;
  customInfo?: { info: IndexInfoType; symbol?: string };
  enableTrading?: boolean;
  capital_protected?: number;
  volatility_buffer_percent?: number | null;
  step?: number | null;
}

type ChartType = Array<{
  date: string;
  open: number;
  low: number;
  high: number;
  close: number;
  volume: number;
}>;
interface IAssetChart {
  // asset: IAsset | null;
  asset: {
    id: number;
    symbol: string;
    type: AssetType | "fx" | "stock" | "bond";
  };
  chart: ChartType;
  weekChart?: ChartType;
  dayChart?: ChartType;
}

export enum FilterType {
  Stock = "stock",
  Bond = "bond",
}
export interface IBondsFilterData {
  currency: null | string[];
  industry: null | string;
  is_eurobond: boolean;
  bond_type: null | string;
  yield_to_matutity: null | {
    from: number;
    to: number;
  };
  maturity_date: null | {
    from: string;
    to: string;
  };
  coupon: null | string;
  reliability_rating: null | number[];
  only_debt_amortization: boolean;
}
export interface IStocksFilterData {
  segment: null | string[];
  industry: null | string[];
  country: null | string[];
  /** id бирж (exchanges.id) */
  exchange: null | number[];
  /** коды валют (USD, EUR, ...) */
  currency: null | string[];
  dividend_yield: null | {
    from: number;
    to: number;
  };
  market_cap: null | {
    from: number;
    to: number;
  };
  sort_by: string;
  order_by: string;
}

type FilterDataType =
  | { data: IBondsFilterData; type: FilterType.Bond }
  | { data: IStocksFilterData; type: FilterType.Stock };

export type IFilter = {
  id: number;
  user_id: number | null;
  user_type: string | null;
  name: string;
  changed?: boolean;
} & FilterDataType;

export type IFavorites = {
  id: number;
  asset: IAsset | null;
  favorite: IAsset | null;
  user_id: number | null;
  user_type: string | null;
  name: string;
  changed?: boolean;
};

export type IResearch = {
  id: number;
  categories: { id: number; name: MultiLangField }[];
  title: MultiLangField;
  subtitle: MultiLangField;
  authors: number[];
  date: string;
  background_image: string;
  alias: string;
  // Записи из Strapi несут автора внутри себя, а не id из /members.
  strapiAuthors?: IMembers[];
  isStrapi?: boolean;
};

export type IResearchByAlias = {
  alias: string;
  id: number;
  categories: { id: number; name: MultiLangField }[];
  title: MultiLangField;
  subtitle: MultiLangField;
  authors: number[];
  date: string;
  background_image: string;
  pdf?: any;
  strapiAuthors?: IMembers[];
  isStrapi?: boolean;
};

export type IBlog = {
  id: number;
  categories: { id: number; name: MultiLangField }[];
  title: MultiLangField;
  subtitle: MultiLangField;
  background_image: string;
  alias: string;
  date: string;
  views: number;
  time_to_read: number;
};

export type IBlogByAlias = {
  alias: string;
  id: number;
  categories: { id: number; name: MultiLangField }[];
  title: MultiLangField;
  subtitle: MultiLangField;
  authors: number[];
  background_image: string;
  date: string;
  views: number;
  time_to_read: number;
  content_en: string;
  content_am: string;
  content_ru: string;
};

export type IMembers = {
  id: number;
  name: MultiLangField;
  role: MultiLangField;
  bio?: MultiLangField;
  image?: string;
};

export interface IFilterDto {
  name: string;
  type: FilterType;
  data: IBondsFilterData | IStocksFilterData;
}

export interface IFavoritesDto {
  asset_id: number;
}

export interface IDepthItem {
  price: number;
  quantity: number;
  yield: number | null;
  operation: OperationType;
  progress: number;
}

export interface IExchange {
  id: number;
  name: string;
  symbol: string;
  pre_open: string | null;
  pre_close: string | null;
  open: string | null;
  close: string | null;
  after_open: string | null;
  after_close: string | null;
  timezone: string;
  logo: string;
  isHoliday: boolean;
  cancel_from: string | null;
}

export enum SpecialOfferStatus {
  Active = "active",
  Inactive = "inactive",
  InProgress = "in_progress",
  Completed = "completed",
  Expired = "expired",
}

export interface ISpecialOffer {
  id: number;
  asset_id: number;
  operation: OperationType;
  price: number;
  volume_from: number;
  volume_to: number;
  volume_currency: string;
  valid_from: string;
  valid_to: string;
  status: SpecialOfferStatus;
  currency: string;
  symbol: string;
}

export type OrderBy = "asc" | "desc";
type SortConfigs = Record<
  number,
  {
    sortBy: string;
    orderBy: OrderBy;
  }
>;

type MarketState = {
  sortConfigs: SortConfigs;
  setSortConfigs: (type: number, configs: any) => void;

  filterSortConfigs: null | {
    sortBy: string;
    orderBy: OrderBy;
  };
  setFilterSortConfigs: (configs: any) => void;

  isLoading: boolean;
  isInitialLoading: boolean;
  assets: any[];
  assetsByType: Record<number, any[]>;
  assetsCountByType: Record<number, number>;
  assetsCurrencies: any[];
  searchAssets: Record<number, any[]>;
  assetsCount: number;
  assetsCurrenciesCount: number;
  activeAssetType: number;
  setActiveAssetType: (type: number) => void;
  loadAssets: (query: GetAssetsListDto) => Promise<void>;

  searchHistory: string[];
  setSearchHistory: (v: string) => void;
  clearSearchHistory: () => void;

  assetInfo: IAssetInfo | null;
  assetChart: IAssetChart | null;
  assetDeals: any[];
  assetDepth: IDepthItem[];
  tariffs: any[];
  exchanges: IExchange[];
  exchangesTimeFrames: Map<string, ExchangeTimeFrame> | null;
  setExchangesTimeFrames: (
    exchangesTimeFrames: Map<string, ExchangeTimeFrame> | null,
  ) => void;
  search: (q: string) => void;
  clearSearchAssets: () => void;
  loadAssetDeals: (id: number) => void;
  newAssetDeal: (deal: any) => void;
  updateAssetDeal: (deal: any) => void;
  loadAssetInfo: (asset_id: any, isBackgroundUpdate?: boolean) => Promise<void>;
  loadAssetDepth: (asset_id: any) => void;
  loadAssetChart: (asset_id: any, custom?: boolean) => Promise<any>;

  getAssetRealTimePrice: (symbol: string) => void;
  setAssetRealTimePrice: (
    symbol: string,
    quote: { ask: number; bid: number; mid: number },
  ) => void;
  checkCurrencyAsset: () => void;
  cancelDeal: (id: number) => void;
  openOrder: (id: number, data: OpenOrderDto, t: any, cb: any) => void;
  openDealReview: (id: number, data: OpenOrderDto) => Promise<any>;
  calculateBondPrice: (
    dto: CalculateBondPriceDto,
  ) => Promise<{ price: number | null; yld: number | null } | null>;
  clearDeals: Function;
  loadTariffs: Function;
  loadExchanges: () => Promise<void>;

  strategies: any[];
  getStrategies: () => Promise<void>;

  filters: IFilter[];
  createdFilter: Partial<IFilter> | null;
  loadFilters: () => Promise<void>;
  createFilter: (body: IFilterDto) => Promise<any>;
  updateFilter: (id: number, body: IFilterDto) => Promise<any>;
  removeFilter: (id: number) => Promise<any>;

  favorites: IFavorites[];
  loadFavorites: () => Promise<void>;
  addFavorites: (body: IFavoritesDto) => Promise<any>;
  removeFavorite: (id: number) => Promise<any>;

  research: IResearch[];
  researchByAlias: IResearchByAlias | null;
  researchCategories: { id: number; name: MultiLangField }[];
  loadResearch: (category_id: number[]) => Promise<void>;
  loadResearchByAlias: (alias: string) => Promise<void>;
  loadResearchCategories: () => Promise<void>;

  blog: IBlog[];
  blogByAlias: IBlogByAlias | null;
  blogCategories: { id: number; name: MultiLangField }[];
  loadBlog: (category_id: number[]) => Promise<void>;
  loadBlogByAlias: (alias: string) => Promise<void>;
  loadBlogCategories: () => Promise<void>;

  members: IMembers[];
  loadMembers: () => Promise<void>;
  activeWtbTab: number;
  setActiveWtbTab: (type: number) => void;

  filterAssets: any[];
  filterAssetsCount: number;
  loadAssetsByFilters: (query: GetAssetsListDto) => Promise<void>;
  preloadAssetsByFilters: (signal: AbortSignal) => Promise<void>;
  loadBondsByFilters: (query: GetAssetsListDto) => Promise<void>;
  preloadBondsByFilters: (signal: AbortSignal) => Promise<void>;

  // load filter fields
  filterStockIndustries: () => Promise<void>;
  stockIndustries: any[];
  filterSectors: () => Promise<void>;
  sectors: any[];
  filterCountries: () => Promise<void>;
  countries: string[];
  loadStockFilterOptions: () => Promise<void>;
  stockFilterOptions: { currencies: string[]; exchanges: number[] };

  loadDiscover: () => Promise<void>;
  discover: any[];
  discoverShowMore: boolean[];
  setDiscoverShowMore: any;

  specialOffers: ISpecialOffer[];
  loadSpecialOffers: () => Promise<void>;

  // market page UI state
  marketScrollState: Record<number, { scroll: number; page: number }>;
  setMarketScrollState: (
    type: number,
    payload: { scroll: number; page: number },
  ) => void;
};

export const marketStore = createStore<MarketState>()(
  persist(
    (set, get) => ({
      marketScrollState: {},
      setMarketScrollState: (type, payload) => {
        set({
          marketScrollState: {
            ...get().marketScrollState,
            [type]: payload,
          },
        });
      },

      sortConfigs: {},
      setSortConfigs: (type: number, configs: any) => {
        set((state) => ({
          sortConfigs: {
            ...state.sortConfigs,
            [type]: configs,
          },
          // when sort changes for a type, clear cached data for that type
          assets: [],
          assetsCount: 0,
          assetsByType: {
            ...state.assetsByType,
            [type]: [],
          },
          assetsCountByType: {
            ...state.assetsCountByType,
            [type]: 0,
          },
        }));
      },

      filterSortConfigs: null,
      setFilterSortConfigs: (v) =>
        set({ filterSortConfigs: v, filterAssets: [], filterAssetsCount: 0 }),

      assetInfo: null,
      assetChart: null,
      isLoading: false,
      isInitialLoading: false,
      assets: [],
      assetsByType: {},
      assetsCountByType: {},
      assetsCurrencies: [],
      assetsCount: 0,
      assetsCurrenciesCount: 0,
      filterAssetsCount: 0,
      discover: [],
      discoverShowMore: [],
      searchAssets: {},
      activeAssetType: 1,
      assetDeals: [],
      assetDepth: [],
      tariffs: [],
      exchanges: [],
      exchangesTimeFrames: null,
      setExchangesTimeFrames: (
        exchangesTimeFrames: Map<string, ExchangeTimeFrame> | null,
      ) => {
        set({ exchangesTimeFrames });
      },
      stockIndustries: [],
      sectors: [],
      filterAssets: [],
      specialOffers: [],
      loadSpecialOffers: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.market.getSpecialOffers();
          if (resp.error) {
          } else {
            const data = resp.data.data;
            const selectedOfferID = fxOrderStore.getState().selectedOfferID;
            if (selectedOfferID) {
              const offerExists = data.find(
                (o: ISpecialOffer) => o.id === selectedOfferID,
              );
              if (!offerExists) {
                fxOrderStore.getState().clear();
              }
            }
            set({ specialOffers: data });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      loadExchanges: async () => {
        try {
          const resp = await sircapApi.dashboard.exchanges();
          if (resp.error) {
          } else {
            set({ exchanges: resp.data.data });
          }
        } catch (error) {}
      },
      setActiveAssetType: (type: number) => {
        // when tab changes, restore cached data for that type (if any)
        set((state) => ({
          activeAssetType: type,
          assets: state.assetsByType[type] || [],
          assetsCount: state.assetsCountByType[type] ?? 0,
        }));
      },
      loadDiscover: async () => {
        try {
          set({ isLoading: get().discover.length > 0 ? false : true });
          const resp = await sircapApi.market.whatToBuy();

          if (resp.error) {
          } else {
            const data = resp.data.data || [];
            set({
              discover: data,
              discoverShowMore: data.map(() => false),
            });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      setDiscoverShowMore: (v: any) => {
        set({ discoverShowMore: v });
      },
      loadAssets: async (query: GetAssetsListDto) => {
        try {
          const isNewTab = query.page === 0;
          const type = query.type;
          const typeNum = Number(type);
          const isCurrencyType = type === String(AssetType.Currency);

          // only show global loading when we don't already have data for this tab
          set({
            isLoading: isCurrencyType
              ? get().assetsCurrencies.length === 0
              : isNewTab && (get().assetsByType[typeNum] || []).length === 0,
          });
          const resp = await sircapApi.market.assets({ params: query });
          if (resp.error) {
          } else {
            // 304 Not Modified: response body may be missing; avoid overwriting state with undefined
            const data = resp.data?.data;
            if (!Array.isArray(data) || data.length < 2) {
              return;
            }
            const [list, count] = data as [any[], number];
            // handle currency list separately (no per-type cache)
            if (isCurrencyType) {
              if (isNewTab) {
                set({
                  assetsCurrencies: list,
                  assetsCurrenciesCount: count,
                });
              } else {
                const lastAssetsCurrencies = get().assetsCurrencies;
                set({
                  assetsCurrencies: [...lastAssetsCurrencies, ...list],
                  assetsCurrenciesCount: count,
                });
              }
            } else {
              if (isNewTab) {
                set((state) => ({
                  assets: list,
                  assetsCount: count,
                  assetsByType: {
                    ...state.assetsByType,
                    [typeNum]: list,
                  },
                  assetsCountByType: {
                    ...state.assetsCountByType,
                    [typeNum]: count,
                  },
                }));
              } else {
                set((state) => {
                  const previous = state.assetsByType[typeNum] || [];
                  const merged = [...previous, ...list];
                  return {
                    assets: merged,
                    assetsCount: count,
                    assetsByType: {
                      ...state.assetsByType,
                      [typeNum]: merged,
                    },
                    assetsCountByType: {
                      ...state.assetsCountByType,
                      [typeNum]: count,
                    },
                  };
                });
              }
            }
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      loadAssetsByFilters: async (query: GetAssetsListDto) => {
        try {
          const isNewTab = query.page === 0;
          set({ isLoading: isNewTab });
          const filter = get().createdFilter?.data || {};
          const resp = await sircapApi.market.getAssetsByFilters(filter, {
            params: query,
          });

          if (resp.error) {
          } else {
            const [list, count] = resp.data.data;
            if (isNewTab) {
              set({ filterAssets: list, filterAssetsCount: count });
            } else {
              const lastAssets = get().filterAssets;
              set({
                filterAssets: [...lastAssets, ...list],
                filterAssetsCount: count,
              });
            }
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      preloadAssetsByFilters: async (signal: AbortSignal) => {
        try {
          set({ isLoading: true });
          const filter = get().createdFilter?.data || {};
          const resp = await sircapApi.market.getAssetsByFilters(filter, {
            signal,
            params: {
              page: 0,
              limit: 1,
            },
          });

          if (!signal.aborted) {
            if (resp.error) {
            } else {
              const [list, count] = resp.data.data;
              set({ filterAssetsCount: count });
            }
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      loadBondsByFilters: async (query: GetAssetsListDto) => {
        try {
          const isNewTab = query.page === 0;
          set({ isLoading: isNewTab });
          const filter = get().createdFilter?.data || {};
          const resp = await sircapApi.market.getBondsByFilters(filter, {
            params: query,
          });

          if (resp.error) {
          } else {
            const [list, count] = resp.data.data;
            if (isNewTab) {
              set({ filterAssets: list, filterAssetsCount: count });
            } else {
              const lastAssets = get().filterAssets;
              set({
                filterAssets: [...lastAssets, ...list],
                filterAssetsCount: count,
              });
            }
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      preloadBondsByFilters: async (signal: AbortSignal) => {
        try {
          set({ isLoading: true });
          const filter = get().createdFilter?.data || {};
          const resp = await sircapApi.market.getBondsByFilters(filter, {
            signal,
            params: {
              page: 0,
              limit: 1,
            },
          });

          if (!signal.aborted) {
            if (resp.error) {
            } else {
              const [list, count] = resp.data.data;
              set({ filterAssetsCount: count });
            }
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },
      search: async (query: string) => {
        set({ isLoading: true });
        try {
          const resp = await sircapApi.market.search(query);
          if (resp.error) {
          } else {
            const list = resp.data.data;
            const searchAssets: Record<number, any[]> = {};
            for (const item of list) {
              const type = item.type;
              if (!(type in searchAssets)) {
                searchAssets[type] = [item];
              } else {
                searchAssets[type].push(item);
              }
            }
            set({ searchAssets });
          }
        } catch (error) {}
        set({ isLoading: false });
      },
      clearSearchAssets: () => {
        set({ searchAssets: [] });
      },
      loadAssetInfo: async (
        asset_id: any,
        isBackgroundUpdate: boolean = false,
      ) => {
        const assetInfoExists = get().assetInfo;
        const isNewAsset =
          assetInfoExists && assetInfoExists?.asset?.id !== asset_id;

        set({
          ...(isNewAsset
            ? {
                isInitialLoading: true,
                isLoading: true,
                assetInfo: null,
                assetChart: null,
                assetDepth: [],
                assetDeals: [],
              }
            : isBackgroundUpdate
            ? {
                isLoading: true,
              }
            : {}),
        });

        const resp = await sircapApi.market.assetInfo(asset_id)({
          params: { platform: Capacitor.getPlatform() || "web" },
        });

        if (resp.error) {
        } else {
          const asset = resp.data.data;
          set({ assetInfo: asset });
        }

        set({
          isLoading: false,
          ...(isNewAsset ? { isInitialLoading: false } : {}),
        });
      },
      loadAssetDepth: async (asset_id: any) => {
        try {
          // set({ assetDepth: [] });
          const resp = await sircapApi.market.assetDepth(asset_id)({
            timeout: 4000,
          });

          if (resp.error) {
          } else {
            const data = resp.data.data;
            set({ assetDepth: data });
          }
        } catch (error) {}
      },

      searchHistory: [],
      setSearchHistory: (v: string) => {
        const newHistory = [v, ...get().searchHistory.filter((i) => i !== v)];
        if (newHistory.length > 12) {
          newHistory.pop();
        }
        set({ searchHistory: newHistory });
      },
      clearSearchHistory: () => {
        set({ searchHistory: [] });
      },

      getAssetRealTimePrice: async (symbol: string) => {
        try {
          const resp = await sircapApi.market.assetPrice(symbol)({
            timeout: 5 * 1000,
          });

          if (resp.error) {
            return null;
          } else {
            const data = resp.data.data;
            const oldAsset = get().assetInfo;
            if (oldAsset && oldAsset.asset) {
              set({
                assetInfo: {
                  ...oldAsset,
                  asset: {
                    ...oldAsset.asset,
                    ask: data.askPrice,
                    bid: data.bidPrice,
                  },
                },
              });
            }
          }
        } catch (error) {
          return null;
        }
      },

      setAssetRealTimePrice: (
        symbol: string,
        quote: { ask: number; bid: number; mid: number },
      ) => {
        const oldAsset = get().assetInfo;
        const stock = symbol.includes("~") ? symbol.split("~")[1] : symbol;
        const exchange = symbol.includes("~") ? symbol.split("~")[0] : null;
        if (oldAsset && oldAsset.asset && oldAsset.asset?.symbol === stock) {
          if (
            oldAsset.asset?.exchange &&
            oldAsset.asset?.exchange?.symbol === exchange
          ) {
            set({
              assetInfo: {
                ...oldAsset,
                asset: {
                  ...oldAsset.asset,
                  ask: quote.ask,
                  bid: quote.bid,
                  // price: quote.mid || oldAsset.asset.price,
                },
              },
            });
          }
        }
      },

      checkCurrencyAsset: () => {
        const oldAsset = get().assetInfo;
        if (
          oldAsset &&
          oldAsset.asset &&
          oldAsset.asset.type === AssetType.Currency
        ) {
          const assets = get().assetsCurrencies;
          const asset = assets.find((a) => a.id === oldAsset.asset!.id);
          if (asset) {
            set({
              assetInfo: {
                ...oldAsset,
                asset: {
                  ...oldAsset.asset,
                  ask: asset.ask,
                  bid: asset.bid,
                },
              },
            });
          }
          // get().loadAssetInfo(oldAsset.asset.id, true); // true = isBackgroundUpdate
        }
      },

      loadAssetDeals: async (id: number) => {
        // set({ isLoading: true });
        try {
          const resp = await sircapApi.market.assetDeals(id)({
            timeout: 5 * 1000,
          });

          if (resp.error) {
          } else {
            set({ assetDeals: resp.data.data });
          }
        } catch (error) {}
        // set({ isLoading: false });
      },
      newAssetDeal: (deal: any) => {
        set({ assetDeals: [deal, ...get().assetDeals] });

        if (
          [
            OperationStatus.Actively,
            OperationStatus.OnPerformance,
            OperationStatus.CancellationRequest,
          ].includes(deal.status)
        ) {
          accountStore.setState({
            activeTransactions: [
              deal,
              ...accountStore.getState().activeTransactions,
            ],
          });
        } else {
          accountStore.setState({
            transactions: [deal, ...accountStore.getState().transactions],
          });
        }
      },
      updateAssetDeal: (deal: any) => {
        if (
          [
            OperationStatus.Error,
            OperationStatus.Error,
            OperationStatus.NotExecuted,
            OperationStatus.Refusal,
          ].includes(deal.status)
        ) {
          toast.warning(`Order #${deal.id} is rejected!`);
        } else if (
          [OperationStatus.NotExecuted, OperationStatus.Expired].includes(
            deal.status,
          )
        ) {
          toast.warning(`Order #${deal.id} is expired!`);
        } else if (deal.status === OperationStatus.Canceled) {
          toast.info(`Order #${deal.id} is cancelled!`);
        } else if (deal.status === OperationStatus.Completed) {
          toast.success(`Order #${deal.id} is completed!`);
        } else if (deal.status === OperationStatus.PartiallyCompleted) {
          toast.success(`Order #${deal.id} is partially completed!`);
        }

        if (
          [
            OperationStatus.Actively,
            OperationStatus.OnPerformance,
            OperationStatus.CancellationRequest,
          ].includes(deal.status)
        ) {
          set({
            assetDeals: get().assetDeals.map((d) =>
              d.id === deal.id ? deal : d,
            ),
          });

          accountStore.setState({
            activeTransactions: accountStore
              .getState()
              .activeTransactions.map((t) => (t.id === deal.id ? deal : t)),
          });
        } else {
          set({
            assetDeals: get().assetDeals.filter((d) => d.id !== deal.id),
          });

          const currTransactions = accountStore.getState().transactions;
          accountStore.setState({
            transactions: currTransactions.find((t) => t.id === deal.id)
              ? currTransactions.map((t) => (t.id === deal.id ? deal : t))
              : [deal, ...currTransactions],
            activeTransactions: accountStore
              .getState()
              .activeTransactions.filter((t) => t.id !== deal.id),
          });
        }
      },

      loadAssetChart: async (asset_id: any, custom: boolean = false) => {
        const assetInfoExists = get().assetChart;
        set({
          ...(assetInfoExists && assetInfoExists?.asset?.id !== asset_id
            ? {
                isLoading: custom ? false : true,
                assetChart: null,
              }
            : {}),
        });

        const resp = await sircapApi.market.assetChart(asset_id)();
        if (resp.error) {
          // pass
        } else {
          const asset = resp.data.data;
          set({ assetChart: asset });
        }
        set({ isLoading: false });
      },

      cancelDeal: async (id: number) => {
        set({ isLoading: true });
        const resp = await sircapApi.market.cancelDeal(id)();

        if (resp.error) {
          toast.error("Server Error");
        } else {
          toast.info("Please wait until we cancel your order");
          set({ assetDeals: resp.data.data });
        }
        set({ isLoading: false });
      },

      openOrder: async (id: number, data: OpenOrderDto, t: any, cb: any) => {
        const promise = new Promise<void>(async (res, rej) => {
          const resp = await sircapApi.market.createDeal(id)(data);

          if (resp.error) {
            cb(resp.error.code || "500");
            rej();
          } else {
            get().loadAssetDeals(id);
            cb();
            res();
          }
        });

        toast.promise(promise, {
          pending: "Pending",
        });
      },

      openDealReview: async (id: number, data: OpenOrderDto) => {
        const resp = await sircapApi.market.reviewDeal(id)(data);

        return resp;
      },

      calculateBondPrice: async (dto: CalculateBondPriceDto) => {
        try {
          const resp = await sircapApi.market.calculateBondPrice(dto);
          if (resp.error) return null;

          const payload = (resp.data as any)?.data;
          if (typeof payload !== "number") return null;

          if (dto.price != null) {
            return {
              price: dto.price != null ? Number(dto.price) : null,
              yld: Number(payload),
            };
          }

          if (dto.yld != null) {
            return {
              price: Number(payload),
              yld: dto.yld != null ? Number(dto.yld) : null,
            };
          }

          return null;
        } catch {
          return null;
        }
      },

      clearDeals: () => {
        set({ assetDeals: [] });
      },

      loadTariffs: async () => {
        const v2Resp = await sircapApi.tariffsV2();
        if (!v2Resp.error && Array.isArray(v2Resp.data.data) && v2Resp.data.data.length) {
          set({ tariffs: v2Resp.data.data });
          return;
        }

        set({ tariffs: [] });
      },

      strategies: [],
      strategy: null,
      getStrategies: async () => {
        try {
          set({ isLoading: get().strategies.length > 0 ? false : true });
          const resp = await sircapApi.market.strategies({});

          if (resp.error) {
          } else {
            const list = resp.data.data;
            set({ strategies: list });
          }
        } catch (error) {
        } finally {
          set({ isLoading: false });
        }
      },

      filters: [],
      createdFilter: null,
      loadFilters: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.market.loadFilters();
          if (resp.error) {
            toast.error("Server Error");
          } else {
            let filters = resp.data.data;
            set({ filters });
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      createFilter: async (body: IFilterDto) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.market.createFilter(body);
          if (resp.error) {
            toast.error("Server Error");
          } else {
            toast.success("Success");
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      updateFilter: async (id: number, body: IFilterDto) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.market.updateFilter(id)(body);
          if (resp.error) {
            toast.error("Server Error");
          } else {
            toast.success("Success");
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      removeFilter: async (id: number) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.market.deleteFilter(id)();
          await get().loadFilters();
          if (resp.error) {
            toast.error("Server Error");
          } else {
            toast.success("Success");
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      filterStockIndustries: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.market.filterStockIndustries();
          if (resp.error) {
            toast.error("Server Error");
          } else {
            let stockIndustries = resp.data.data;
            set({ stockIndustries });
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      filterSectors: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.market.filterSectors();
          if (resp.error) {
            toast.error("Server Error");
          } else {
            let sectors = resp.data.data;
            set({ sectors });
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      countries: [],
      stockFilterOptions: { currencies: [], exchanges: [] },
      loadStockFilterOptions: async () => {
        try {
          const resp = await sircapApi.market.filterStockOptions();
          if (!resp.error) {
            set({ stockFilterOptions: resp.data.data });
          }
        } catch (error) {}
      },
      filterCountries: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.market.filterCountries();
          if (resp.error) {
            toast.error("Server Error");
          } else {
            let countries = resp.data.data;
            set({ countries });
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },

      favorites: [],
      loadFavorites: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.whatToBuy.loadFavorites();
          if (resp.error) {
            toast.error("Server Error");
          } else {
            let favorites = resp.data.data;
            set({ favorites });
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },

      research: [],
      researchByAlias: null,
      researchCategories: [],
      loadResearch: async (category_id: number[]) => {
        try {
          set({ isLoading: get().research.length > 0 ? false : true });
          const resp = await sircapApi.whatToBuy.loadResearch({
            params: {
              category_id: category_id,
            },
          });
          // console.log(resp + " - loadResearch"); // * Debugging
          if (resp.error) {
            toast.error("Server Error");
          } else {
            const legacy: IResearch[] = resp.data.data ?? [];
            // Записи старой админки остаются, свежие приходят из Strapi и
            // встают в тот же список по дате. При фильтре по категориям их не
            // добавляем: категорий у них нет, под фильтр они не подпадают.
            let research = legacy;
            if (!category_id || category_id.length === 0) {
              const strapiResp = await strapiApi.researches();
              const strapi = normalizeStrapiResearchList(
                strapiResp.data?.data,
              );
              const seen = new Set(legacy.map((i) => i.alias));
              research = [
                ...legacy,
                ...strapi.filter((i) => !seen.has(i.alias)),
              ].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
            }
            set({ research });
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      loadResearchByAlias: async (alias: any) => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.whatToBuy.loadResearchByAlias(alias)();
          if (resp.error || !resp.data?.data) {
            // Алиас, которого нет в старом бэкенде, — это отчёт из Strapi.
            const strapiResp = await strapiApi.researchByAlias(alias);
            const raw = strapiResp.data?.data;
            if (raw?.id) {
              set({ researchByAlias: toLegacyResearchByAlias(raw) });
            } else {
              toast.error("Failed to load research details");
            }
          } else {
            set({ researchByAlias: resp.data.data });
          }
        } catch (error) {
          toast.error("Failed to load research details");
        } finally {
          set({ isLoading: false });
        }
      },
      loadResearchCategories: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.whatToBuy.loadResearchCategories();
          if (resp.error) {
            toast.error("Server Error");
          } else {
            let researchCategories = resp.data.data;
            set({ researchCategories });
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },

      blog: [],
      blogByAlias: null,
      blogCategories: [],
      // Блог целиком переехал в Strapi: все посты старой админки уже там, так
      // что читаем только CMS. Категорий у постов на ru/hy в CMS нет, поэтому
      // фильтр по категориям отрабатывает локально по английским значениям.
      loadBlog: async (category_id: number[]) => {
        try {
          set({ isLoading: get().blog.length > 0 ? false : true });
          const batches = await Promise.all(
            BLOG_LOCALES.map(async ([lang, locale]) => {
              const resp = await strapiApi.blogs(locale, BLOG_LIST_FIELDS);
              return [lang, resp.data?.data ?? []] as const;
            }),
          );
          const blog = foldBlogLocales(batches);
          set({
            blog: category_id?.length
              ? blog.filter((b) =>
                  b.categories.some((c) => category_id.includes(c.id)),
                )
              : blog,
          });
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      loadBlogByAlias: async (alias: any) => {
        try {
          set({ isLoading: true });
          // Без fields — тело статьи лежит в `summary`, а его нужно получить.
          const batches = await Promise.all(
            BLOG_LOCALES.map(async ([lang, locale]) => {
              const resp = await strapiApi.blogs(locale, {
                "filters[alias][$eq]": alias,
              });
              return [lang, resp.data?.data ?? []] as const;
            }),
          );
          const item = foldBlogLocales(batches)[0];
          if (item) {
            set({ blogByAlias: item });
          } else {
            toast.error("Failed to load blog details");
          }
        } catch (error) {
          toast.error("Failed to load blog details");
        } finally {
          set({ isLoading: false });
        }
      },
      loadBlogCategories: async () => {
        try {
          set({ isLoading: true });
          const resp = await strapiApi.blogs("en", BLOG_LIST_FIELDS);
          set({ blogCategories: collectBlogCategories(resp.data?.data ?? []) });
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },

      activeWtbTab: parseInt(localStorage.getItem("activeWtbTab") || "1", 10),
      setActiveWtbTab: (id: number) => {
        localStorage.setItem("activeWtbTab", id.toString());
        set({ activeWtbTab: id });
      },
      members: [],
      loadMembers: async () => {
        try {
          set({ isLoading: true });
          const resp = await sircapApi.whatToBuy.loadMembers();
          if (resp.error) {
            toast.error("Server Error");
          } else {
            let members = resp.data.data;
            set({ members });
          }
        } catch (error) {
          toast.error("Server Error");
        } finally {
          set({ isLoading: false });
        }
      },
      addFavorites: async (body: IFavoritesDto) => {
        try {
          // set({ isLoading: true });
          const resp = await sircapApi.whatToBuy.addFavorites(body);
          //!rerender issue
          // await get().loadFavorites();
        } catch (error) {
          toast.error("Server Error");
        } finally {
          // set({ isLoading: false });
        }
      },
      removeFavorite: async (id: number) => {
        try {
          // set({ isLoading: true });
          const resp = await sircapApi.whatToBuy.removeFavorite(id)();
          // await get().loadFavorites();
        } catch (error) {
          toast.error("Server Error");
        } finally {
          // set({ isLoading: false });
        }
      },
    }),
    {
      name: "market",
      version: 3,
      merge: (persistedState, currState) => {
        const {
          sortConfigs,
          activeAssetType,
          tariffs,
          searchHistory,
          createdFilter,
        } = persistedState as Partial<MarketState>;

        return {
          ...currState,
          sortConfigs: sortConfigs || {},
          activeAssetType: activeAssetType || 1,
          tariffs: tariffs?.length ? tariffs : [],
          searchHistory: searchHistory?.length ? searchHistory : [],
          createdFilter: createdFilter || null,
        };
      },
    },
  ),
);

export const useSortConfigs = () =>
  useStore(marketStore, (state) => state.sortConfigs);

export const useTariff = () => {
  const user = useCurrentUser();
  const { tariffs } = useStore(marketStore);

  try {
    return tariffs.find(({ id }) => id === user?.tariff);
  } catch (error) {
    return null;
  }
};

export const useIsExchangeActive = (ex: number | undefined) => {
  try {
    // const IS_DEV_MODE = process.env.REACT_APP_DEV_MODE === "true";
    // if (IS_DEV_MODE) return true;
    // if day is weekend all exchanges are closed
    const weekday = moment().format("dddd");
    const isWeekend = ["Sunday", "Saturday"].includes(weekday);
    if (isWeekend) return false;

    const exchange = marketStore.getState().exchanges.find((e) => e.id === ex);
    if (!exchange) return false;

    if (!exchange.open || !exchange.close || !exchange.timezone) {
      return false;
    }

    const { open, close, timezone } = exchange;

    // moment.tz.setDefault(timeZone);
    const startTime = moment(open, "HH:mm").tz(timezone, true);
    const endTime = moment(close, "HH:mm").tz(timezone, true);

    const nowTime = moment().tz(timezone).set("date", startTime.get("date"));

    if (nowTime.isBetween(startTime, endTime)) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

export const useSelectFilters = (
  type: FilterType,
  author: "user" | "admin",
) => {
  const { filters } = useStore(marketStore);

  return filters
    .filter((f) => f.type === type)
    .filter((f) =>
      author === "admin" ? f.user_id === null : f.user_id !== null,
    );
};

// filter

export {
  isQuantityOffStep,
  type QuantityStepInput,
} from "./lib/quantity-step";
